import type { IncomingMaterialsTranslation } from './types'

/**
 * English translations for Incoming Materials domain
 */
export const incoming_materialsTranslationEn: IncomingMaterialsTranslation = {
  // Page/Section level
  title: 'Incoming Materials',
  subtitle: 'Manage purchase orders, suppliers, and goods receipts',
  description: 'Manage incoming materials in the system',

  // Purchase Order
  purchaseOrder: 'Purchase Order',
  poNumber: 'PO Number',
  poDate: 'PO Date',
  poDetails: 'Purchase Order Details',
  poLinesTitle: 'Order Lines',
  poLinesHint: 'Add materials to this purchase order',

  // PO Status
  poStatus: {
    draft: 'Draft',
    issued: 'Issued',
    closed: 'Closed',
    cancelled: 'Cancelled',
    pending: 'Pending',
    completed: 'Completed',
  },
  factory: 'Factory',
  materialGroup: 'Material Group',

  // Receipt Status
  receiptStatus: {
    pending: 'Pending Receipt',
    partial: 'Partially Received',
    complete: 'Complete',
  },

  // Line Status
  status: {
    pending: 'Pending',
    partial: 'Partial',
    accepted: 'Accepted',
    rejected: 'Rejected',
    disputed: 'Disputed',
  },

  // Create/Actions
  createTitle: 'Create Purchase Order',
  createHint: 'Create a new purchase order for incoming materials',
  createPurchaseOrder: 'Create Purchase Order',
  createPOHint: 'Add a new purchase order to track incoming materials',
  createSupplier: 'Create Supplier',
  createMaterial: 'Create Material',
  createMaterialHint: 'Add a new material to the catalog',
  createFirstPO: 'Create Your First Purchase Order',
  receiveGoods: 'Receive Goods',
  receiveHint: 'Record goods receipt against a purchase order',
  confirmReceipt: 'Confirm Receipt',
  sendToAds: 'Send to ADS',
  sendingToAds: 'Sending to ADS...',
  sendToAdsSuccess: 'Purchase order sent to ADS',
  sendToAdsFailure: 'Could not send purchase order to ADS',

  // Lines
  linesTitle: 'Order Lines',
  linesHint: 'Materials to be received',
  addLine: 'Add Line',
  removeLine: 'Remove Line',
  receiveLines: 'Receive Lines',
  receiveLinesHint: 'Enter received quantities for each line',
  noPoLines: 'No lines in this purchase order',

  // Supplier
  supplier: 'Supplier',
  supplierName: 'Supplier Name',
  supplierCode: 'Supplier Code',
  taxId: 'Tax ID / VAT Number',
  noSupplier: 'No supplier selected',

  // Material
  materialName: 'Material Name',
  materialCode: 'Material Code',
  materialDescription: 'Material Description',

  // Fields
  fields: {
    supplier: 'Supplier',
    purchaseOrder: 'Purchase Order',
    purchaseOrderLine: 'PO Line',
    material: 'Material',
    quantity: 'Quantity',
    status: 'Status',
    receivedAt: 'Received At',
    driverName: 'Driver Name',
    driverPhone: 'Driver Phone',
    vehiclePlate: 'Vehicle Plate',
    supplierDeliveryNoteNo: 'Delivery Note No.',
    notes: 'Notes',
  },

  // Placeholders
  placeholders: {
    selectSupplier: 'Select a supplier',
    supplierName: 'Enter supplier name',
    selectPurchaseOrder: 'Select a purchase order',
    selectPurchaseOrderLine: 'Select a PO line',
    selectMaterial: 'Select a material',
    materialName: 'Enter material name',
    materialDescription: 'Enter material description',
    deliveryNote: 'Enter delivery note number',
    driverName: 'Enter driver name',
    driverPhone: 'Enter driver phone',
    vehiclePlate: 'Enter vehicle plate',
    notes: 'Enter notes',
  },

  // Validation
  validation: {
    supplierNameRequired: 'Supplier name is required',
    supplierCodeRequired: 'Supplier code is required',
  },

  // Table
  table: {
    materials: 'Materials',
    receiptStatus: 'Receipt Status',
    moreItems: 'more items',
    showing: 'Showing {{start}}-{{end}} of {{total}}',
    perPage: 'Per page',
  },

  filters: {
    title: 'Filters',
    all: 'All',
    apply: 'Apply',
    clearAll: 'Clear all',
    status: 'Status',
    supplier: 'Supplier',
    material: 'Material',
    supplierSearchPlaceholder: 'Search supplier...',
    materialSearchPlaceholder: 'Search material...',
    loading: 'Loading...',
    noSuppliers: 'No suppliers found',
    noMaterials: 'No materials found',
  },

  // Other
  expectedDeliveryDate: 'Expected Delivery Date',
  uom: 'Unit of Measure',
  defaultUom: 'EA',
  overallProgress: 'Overall Progress',
  received: 'Received',
  complete: 'Complete',
  receiptsHistory: 'Receipts History',
  notesPlaceholder: 'Add notes about this receipt...',
  searchPOs: 'Search purchase orders...',
  noPurchaseOrders: 'No purchase orders found',
  noMatchingPOs: 'No matching purchase orders',
  totalPOs: 'Total Purchase Orders',
  filteredPOs: 'Filtered Purchase Orders',
  showingPOs: 'Showing Purchase Orders',
  items: 'items',
  receipts: 'receipts',
  receivedQty: 'Received Qty: {{qty}} {{uom}}',
  orderedVsReceived: 'Ordered vs Received',
  selectedSupplier: 'Selected Supplier',

  // Actions (legacy)
  actions: {
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    save: 'Save',
    cancel: 'Cancel',
  },

  // Form (legacy)
  form: {
    name: 'Name',
    description: 'Description',
    status: 'Status',
  },

  // Messages
  messages: {
    created: 'Incoming material created successfully',
    updated: 'Incoming material updated successfully',
    deleted: 'Incoming material deleted successfully',
    error: 'An error occurred',
  },

  // Inbound Shipments
  kioskInbound: {
    title: 'Delivering to us?',
    subtitle: 'Register here if the gate could not read your QR or your plate.',
    plateLabel: 'Your plate number',
    plateDigitsPlaceholder: '1234',
    plateLettersPlaceholder: 'ABC',
    poNumberLabel: 'Purchase order number',
    poNumberPlaceholder: 'e.g. 4500000219',
    lineNumberLabel: 'Line item number',
    lineNumberPlaceholder: 'e.g. 0010',
    scanHint: 'Have a QR on your paperwork? Scan it to fill these in.',
    scanNotRecognised: 'That code could not be read. Try again, or type the numbers below.',
    findLoads: 'Continue',
    remainingOf: '{{remaining}} of {{ordered}} {{uom}} left',
    register: 'Register my arrival',
    registering: 'Registering…',
    startOver: 'Start over',
    successTitle: "You're registered",
    successHint: 'Take your slip and drive to the gate. It should open for your plate.',
    printAgain: 'Print slip again',
    poNotFound: 'No purchase order with that number.',
    lineNotOnPo: 'Line {{line}} is not on this purchase order.',
    lineFullyReceived: 'Line {{line}} has already been fully delivered.',
  },

  inboundShipments: {
    title: 'Inbound Shipments',
    subtitle: 'Track and manage inbound shipment arrivals and weighbridge operations',
    searchPlaceholder: 'Search by shipment #, plate, or PO number...',
    noShipments: 'No inbound shipments found',
    noMatchingShipments: 'No shipments match your search or filter',
    totalShipments: 'Total Shipments',
    showingShipments: 'Showing shipments',
    shipmentNumber: 'Shipment Number',
    vehiclePlate: 'Vehicle Plate',
    driver: 'Driver',
    arrivedAt: 'Arrived At',
    grossWeight: 'Gross Weight (kg)',
    tareWeight: 'Tare Weight (kg)',
    netWeight: 'Net Weight (kg)',
    firstWeightAt: 'First Weight At',
    secondWeightAt: 'Second Weight At',
    grnNumber: 'GRN Number',
    sapMaterialDocument: 'SAP Material Document',
    sapSyncStatus: 'SAP Sync Status',
    purchaseOrder: 'Purchase Order',
    supplier: 'Supplier',
    gate: 'Gate',
    statusFilter: {
      all: 'All',
      arrived: 'Arrived',
      first_weight_done: 'First Weight Done',
      unloaded: 'Unloaded',
      second_weight_done: 'Second Weight Done',
      grn_posted: 'GRN Posted',
      grn_skipped: 'GRN Skipped',
    },
    sapSyncStatusLabel: {
      pending: 'Pending',
      first_weight_synced: 'First Weight Synced',
      second_weight_synced: 'Second Weight Synced',
      grn_synced: 'GRN Synced',
      failed: 'Failed',
    },
    filters: {
      title: 'Filters',
      all: 'All',
      apply: 'Apply',
      clearAll: 'Clear all',
      status: 'Status',
      supplier: 'Supplier',
      material: 'Material',
      supplierSearchPlaceholder: 'Search supplier...',
      materialSearchPlaceholder: 'Search material...',
      loading: 'Loading...',
      noSuppliers: 'No suppliers found',
      noMaterials: 'No materials found',
      sapSyncStatus: 'SAP Sync',
      grnStatus: 'GRN',
      weightStatus: 'Weight',
    },
    grnFilter: {
      all: 'All GRNs',
      posted: 'GRN Posted',
      not_posted: 'No GRN',
    },
    weightFilter: {
      all: 'All Weights',
      gross_recorded: 'Gross Recorded',
      net_recorded: 'Net Recorded',
      missing_weight: 'Missing Weight',
    },
    columns: {
      shipmentNumber: 'Shipment #',
      vehiclePlate: 'Vehicle Plate',
      purchaseOrder: 'PO Number',
      material: 'Material',
      status: 'Status',
      grossWeight: 'Gross (kg)',
      netWeight: 'Net (kg)',
      sapSyncStatus: 'SAP Sync',
      grnNumber: 'Grn Number',
      arrivedAt: 'Arrived At',
    },
    detail: {
      title: 'Shipment Details',
      overview: 'Overview',
      timeline: 'Timeline',
      shipmentInfo: 'Shipment Information',
      weightInfo: 'Weight Information',
      syncInfo: 'SAP Sync',
      driverName: 'Driver Name',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      departedAt: 'Departed At',
      factory: 'Factory',
      storageLocation: 'Storage Location',
      poNumber: 'PO Number',
      sapPoNumber: 'SAP PO Number',
      sapItemNumber: 'SAP Item',
      material: 'Material',
      orderedQuantity: 'Ordered Quantity',
      ticketNumber: 'Ticket Number',
      firstWeightSyncedAt: 'First Weight Synced At',
      secondWeightSyncedAt: 'Second Weight Synced At',
      grnSyncedAt: 'GRN Synced At',
      error: 'Error',
      success: 'Success',
      failed: 'Failed',
      timelineEvents: 'events',
      noTimeline: 'No timeline events yet',
      timelineError: 'Timeline failed to load',
      refreshTimeline: 'Refresh timeline',
      timelineStep: 'Step',
      timelineStatus: 'Status',
      weightKg: 'Weight',
      netWeightKg: 'Net Weight',
      timelineEmpty: 'No timeline events yet',
      timelineLoadError: 'Unable to load timeline',
      timelineSuccess: 'Success',
      timelineFailed: 'Failed',
      timelineRefresh: 'Refresh',
      timelineActions: {
        CREATED: 'Created',
        GATE_OPENED: 'Gate opened',
        WEIGHBRIDGE_TRIGGERED: 'Weighbridge triggered',
        INBOUND_FIRST_WEIGHT: 'First weight',
        INBOUND_SECOND_WEIGHT: 'Second weight',
        INBOUND_GRN: 'GRN',
      },
    },
  },
}
